import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Calculator, Droplet, Scale, ArrowRightLeft, Info, ExternalLink } from 'lucide-react';

const DosageCalculator = ({ peptidesDatabase = [], protocolLibrary = [] }) => {
  // Reconstitution Calculator State
  const [vialSize, setVialSize] = useState('5');
  const [vialSizeUnit, setVialSizeUnit] = useState('mg');
  const [bacteriostaticWater, setBacteriostaticWater] = useState('2');
  const [concentration, setConcentration] = useState(null);

  // Dosage Calculator State
  const [targetDose, setTargetDose] = useState('250');
  const [targetDoseUnit, setTargetDoseUnit] = useState('mcg');
  const [solutionConcentration, setSolutionConcentration] = useState('2500');
  const [injectionVolume, setInjectionVolume] = useState(null);
  const [injectionUnits, setInjectionUnits] = useState(null);

  // Weight-Based Dosing State
  const [patientWeight, setPatientWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [dosePerKg, setDosePerKg] = useState('');
  const [dosePerKgUnit, setDosePerKgUnit] = useState('mcg');
  const [totalDoseNeeded, setTotalDoseNeeded] = useState(null);
  const [calcError, setCalcError] = useState('');

  // Handle weight unit toggle with automatic value conversion
  const handleWeightUnitChange = (newUnit) => {
    if (patientWeight && newUnit !== weightUnit) {
      const currentValue = parseFloat(patientWeight);
      if (!isNaN(currentValue) && currentValue > 0) {
        if (newUnit === 'kg' && weightUnit === 'lbs') {
          setPatientWeight((currentValue / 2.20462).toFixed(1));
        } else if (newUnit === 'lbs' && weightUnit === 'kg') {
          setPatientWeight(String(Math.round(currentValue * 2.20462)));
        }
      }
    }
    setWeightUnit(newUnit);
  };

  // Peptide-Specific Calculator State
  const [selectedPeptide, setSelectedPeptide] = useState('');
  const [peptideInfo, setPeptideInfo] = useState(null);
  const [relatedProtocols, setRelatedProtocols] = useState([]);

  // Unit Conversion State
  const [convertValue, setConvertValue] = useState('');
  const [convertFromUnit, setConvertFromUnit] = useState('mg');
  const [convertToUnit, setConvertToUnit] = useState('mcg');
  const [convertedValue, setConvertedValue] = useState(null);

  // Reject negative numbers in inputs
  const positiveOnly = (setter) => (e) => {
    const val = e.target.value;
    if (val === '' || val === '.' || parseFloat(val) >= 0) setter(val);
  };

  // Calculate Reconstitution
  const calculateReconstitution = () => {
    const vs = parseFloat(vialSize);
    const bw = parseFloat(bacteriostaticWater);
    if (!vs || !bw || vs <= 0 || bw <= 0 || isNaN(vs) || isNaN(bw)) {
      setConcentration(null);
      setCalcError('Please enter values greater than 0');
      return;
    }
    setCalcError('');
    
    const vialSizeMcg = vialSizeUnit === 'mg' ? parseFloat(vialSize) * 1000 : parseFloat(vialSize);
    const waterMl = parseFloat(bacteriostaticWater);
    
    const concMcgPerMl = vialSizeMcg / waterMl;
    const concMgPerMl = concMcgPerMl / 1000;
    
    const result = {
      mcgPerMl: concMcgPerMl.toFixed(2),
      mgPerMl: concMgPerMl.toFixed(4),
      totalVolume: waterMl
    };
    
    setConcentration(result);
  };

  // Calculate Dosage
  const calculateDosage = () => {
    const td = parseFloat(targetDose);
    const sc = parseFloat(solutionConcentration);
    if (!td || !sc || td <= 0 || sc <= 0 || isNaN(td) || isNaN(sc)) {
      setCalcError('Please enter values greater than 0');
      return;
    }
    setCalcError('');
    
    const doseMcg = targetDoseUnit === 'mg' ? parseFloat(targetDose) * 1000 : parseFloat(targetDose);
    const concMcgPerMl = parseFloat(solutionConcentration);
    
    const volumeMl = doseMcg / concMcgPerMl;
    const volumeUnits = volumeMl * 100; // 1ml = 100 units on insulin syringe
    
    setInjectionVolume(volumeMl.toFixed(3));
    setInjectionUnits(volumeUnits.toFixed(1));
  };

  // Calculate Weight-Based Dosing
  const calculateWeightBased = () => {
    
    if (!patientWeight) {
      console.warn('Missing weight value');
      return;
    }

    // If only weight entered (no dosePerKg), show weight conversion as result
    const pw = parseFloat(patientWeight);
    if (isNaN(pw) || pw <= 0) return;

    const unit = weightUnit || 'lbs'; // Fallback to 'lbs' if state somehow not set
    const weightKg = unit === 'lbs' ? pw / 2.20462 : pw;

    if (!dosePerKg) {
      // Just show weight conversion
      setTotalDoseNeeded({
        mcg: `${weightKg.toFixed(1)} kg`,
        mg: unit === 'lbs' ? `${pw} lbs = ${weightKg.toFixed(1)} kg` : `${pw} kg = ${Math.round(pw * 2.20462)} lbs`
      });
      return;
    }

    const dosePerKgValue = parseFloat(dosePerKg);
    if (isNaN(dosePerKgValue) || dosePerKgValue <= 0) return;
    
    let totalDose;
    const dkUnit = dosePerKgUnit || 'mcg';
    if (dkUnit === 'mcg') {
      totalDose = {
        mcg: (weightKg * dosePerKgValue).toFixed(2),
        mg: (weightKg * dosePerKgValue / 1000).toFixed(4)
      };
    } else {
      totalDose = {
        mg: (weightKg * dosePerKgValue).toFixed(4),
        mcg: (weightKg * dosePerKgValue * 1000).toFixed(2)
      };
    }
    
    setTotalDoseNeeded(totalDose);
  };

  // Unit Conversion
  const performUnitConversion = () => {
    if (!convertValue) return;
    
    const value = parseFloat(convertValue);
    let result;
    
    // Convert to base unit (mcg)
    let valueMcg;
    if (convertFromUnit === 'mg') valueMcg = value * 1000;
    else if (convertFromUnit === 'mcg') valueMcg = value;
    else if (convertFromUnit === 'g') valueMcg = value * 1000000;
    else if (convertFromUnit === 'IU') valueMcg = value * 0.0347; // approximate for peptides
    
    // Convert from base unit to target
    if (convertToUnit === 'mg') result = (valueMcg / 1000).toFixed(4);
    else if (convertToUnit === 'mcg') result = valueMcg.toFixed(2);
    else if (convertToUnit === 'g') result = (valueMcg / 1000000).toFixed(6);
    else if (convertToUnit === 'IU') result = (valueMcg / 0.0347).toFixed(2);
    
    // Special conversions for volume and weight
    if (convertFromUnit === 'ml' && convertToUnit === 'units') result = (value * 100).toFixed(1);
    if (convertFromUnit === 'units' && convertToUnit === 'ml') result = (value / 100).toFixed(3);
    if (convertFromUnit === 'lbs' && convertToUnit === 'kg') result = (value / 2.20462).toFixed(1);
    if (convertFromUnit === 'kg' && convertToUnit === 'lbs') result = String(Math.round(value * 2.20462));
    
    setConvertedValue(result);
  };

  // Load peptide-specific information
  useEffect(() => {
    if (selectedPeptide && peptidesDatabase.length > 0) {
      const peptide = peptidesDatabase.find(p => p.name === selectedPeptide);
      setPeptideInfo(peptide);
      
      // Find related protocols
      const related = protocolLibrary.filter(protocol => 
        protocol.name?.toLowerCase().includes(selectedPeptide.toLowerCase()) ||
        protocol.primary_peptides?.includes(selectedPeptide)
      );
      setRelatedProtocols(related);
    }
  }, [selectedPeptide, peptidesDatabase, protocolLibrary]);

  // Auto-populate solution concentration when reconstitution is calculated
  useEffect(() => {
    if (concentration && concentration.mcgPerMl) {
      setSolutionConcentration(concentration.mcgPerMl);
    }
  }, [concentration]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-6 mb-8">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Calculator className="h-10 w-10 text-blue-600 flex-shrink-0" />
          <h1 className="text-3xl md:text-4xl font-bold">Peptide Dosage Calculator</h1>
        </div>
        <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
          Professional-grade calculator for peptide reconstitution, dosing, and administration. 
          Calculate precise dosages, convert units, and access peptide-specific recommendations.
        </p>
        
        {/* Workflow Guide */}
        <Alert className="max-w-4xl mx-auto bg-blue-50 border-blue-200">
          <AlertDescription>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-blue-800">💡 Quick Guide:</span>
              <span className="text-blue-700">
                Step 1: Calculate Reconstitution → 
                Step 2: Auto-fills Dosage Calculator → 
                Step 3: Calculate Injection Volume
              </span>
            </div>
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Calculator Tabs */}
      <Tabs defaultValue="reconstitution" className="space-y-6">
        <TabsList className="flex flex-wrap justify-center gap-2 h-auto p-2 bg-muted">
          <TabsTrigger value="reconstitution" className="flex items-center gap-1 px-3 py-2">
            <Droplet className="h-4 w-4" />
            <span className="hidden sm:inline">Reconstitution</span>
          </TabsTrigger>
          <TabsTrigger value="dosage" className="flex items-center gap-1 px-3 py-2">
            <Calculator className="h-4 w-4" />
            <span className="hidden sm:inline">Dosage</span>
          </TabsTrigger>
          <TabsTrigger value="weight" className="flex items-center gap-1 px-3 py-2">
            <Scale className="h-4 w-4" />
            <span className="hidden sm:inline">Weight-Based</span>
          </TabsTrigger>
          <TabsTrigger value="peptide" className="flex items-center gap-1 px-3 py-2">
            <Info className="h-4 w-4" />
            <span className="hidden sm:inline">Peptide-Specific</span>
          </TabsTrigger>
          <TabsTrigger value="convert" className="flex items-center gap-1 px-3 py-2">
            <ArrowRightLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Conversions</span>
          </TabsTrigger>
        </TabsList>

        {/* Reconstitution Calculator */}
        <TabsContent value="reconstitution">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Droplet className="h-5 w-5 text-blue-600" />
                Reconstitution Calculator
              </CardTitle>
              <CardDescription>
                Calculate the concentration after reconstituting your peptide vial with bacteriostatic water
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="vialSize">Peptide Vial Size</Label>
                  <div className="flex gap-2">
                    <Input
                      id="vialSize"
                      type="number" min="0.01"
                      placeholder="5"
                      value={vialSize}
                      onChange={positiveOnly(setVialSize)}
                      className="flex-1"
                    />
                    <Select value={vialSizeUnit} onValueChange={setVialSizeUnit}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mg">mg</SelectItem>
                        <SelectItem value="mcg">mcg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bacteriostaticWater">Bacteriostatic Water Volume (ml)</Label>
                  <Input
                    id="bacteriostaticWater"
                    type="number" min="0.01"
                    placeholder="2"
                    value={bacteriostaticWater}
                    onChange={positiveOnly(setBacteriostaticWater)}
                  />
                </div>
              </div>

              <Button onClick={calculateReconstitution} className="w-full">
                Calculate Concentration
              </Button>
              {calcError && <p className="text-red-600 text-sm font-medium mt-2" data-testid="calc-error">{calcError}</p>}

              {concentration && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="text-lg font-semibold text-green-800">Final Concentration:</div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-3 bg-white rounded border border-green-200">
                          <div className="text-2xl font-bold text-green-700">{concentration.mcgPerMl} mcg/ml</div>
                          <div className="text-sm text-green-600">Micrograms per milliliter</div>
                        </div>
                        <div className="p-3 bg-white rounded border border-green-200">
                          <div className="text-2xl font-bold text-green-700">{concentration.mgPerMl} mg/ml</div>
                          <div className="text-sm text-green-600">Milligrams per milliliter</div>
                        </div>
                      </div>
                      <div className="text-sm text-green-700 mt-2">
                        Total solution volume: {concentration.totalVolume} ml
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dosage Calculator */}
        <TabsContent value="dosage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-600" />
                Dosage Calculator
              </CardTitle>
              <CardDescription>
                Calculate the injection volume needed for your target dose
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="targetDose">Target Dose</Label>
                  <div className="flex gap-2">
                    <Input
                      id="targetDose"
                      type="number" min="0.01"
                      placeholder="250"
                      value={targetDose}
                      onChange={positiveOnly(setTargetDose)}
                      className="flex-1"
                    />
                    <Select value={targetDoseUnit} onValueChange={setTargetDoseUnit}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcg">mcg</SelectItem>
                        <SelectItem value="mg">mg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="solutionConcentration">
                    Solution Concentration (mcg/ml)
                    {concentration && (
                      <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                        Auto-filled from Reconstitution
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="solutionConcentration"
                    type="number" min="0.01"
                    placeholder="2500"
                    value={solutionConcentration}
                    onChange={positiveOnly(setSolutionConcentration)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {concentration 
                      ? '✅ Value auto-populated from Reconstitution tab' 
                      : 'Calculate Reconstitution first, or enter manually'}
                  </p>
                </div>
              </div>

              <Button onClick={calculateDosage} className="w-full">
                Calculate Injection Volume
              </Button>
              {calcError && <p className="text-red-600 text-sm font-medium mt-2">{calcError}</p>}

              {injectionVolume && (
                <Alert className="bg-purple-50 border-purple-200">
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="text-lg font-semibold text-purple-800">Injection Volume Needed:</div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-3 bg-white rounded border border-purple-200">
                          <div className="text-2xl font-bold text-purple-700">{injectionVolume} ml</div>
                          <div className="text-sm text-purple-600">Milliliters</div>
                        </div>
                        <div className="p-3 bg-white rounded border border-purple-200">
                          <div className="text-2xl font-bold text-purple-700">{injectionUnits} units</div>
                          <div className="text-sm text-purple-600">Insulin Syringe Units</div>
                        </div>
                      </div>
                      <div className="text-sm text-purple-700 mt-2">
                        💉 Use an insulin syringe (100 units = 1 ml)
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weight-Based Dosing */}
        <TabsContent value="weight">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-orange-600" />
                Weight-Based Dosing Calculator
              </CardTitle>
              <CardDescription>
                Calculate total dose needed based on patient body weight
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="patientWeight">Patient Weight</Label>
                  <div className="flex gap-2">
                    <Input
                      id="patientWeight"
                      type="number" min="0.01"
                      placeholder="180"
                      value={patientWeight}
                      onChange={positiveOnly(setPatientWeight)}
                      className="flex-1"
                      data-testid="patient-weight-input"
                    />
                    <Select value={weightUnit} onValueChange={handleWeightUnitChange}>
                      <SelectTrigger className="w-24" data-testid="weight-unit-toggle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lbs">lbs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dosePerKg">Dose per Kilogram</Label>
                  <div className="flex gap-2">
                    <Input
                      id="dosePerKg"
                      type="number" min="0.01"
                      placeholder="3.5"
                      value={dosePerKg}
                      onChange={positiveOnly(setDosePerKg)}
                      className="flex-1"
                    />
                    <Select value={dosePerKgUnit} onValueChange={setDosePerKgUnit}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcg">mcg/kg</SelectItem>
                        <SelectItem value="mg">mg/kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Button onClick={calculateWeightBased} className="w-full" data-testid="calculate-weight-based-btn">
                Calculate Total Dose
              </Button>

              {patientWeight && weightUnit && (
                <p className="text-xs text-muted-foreground text-center">
                  {weightUnit === 'lbs' 
                    ? `${patientWeight} lbs = ${(parseFloat(patientWeight) / 2.20462).toFixed(1)} kg`
                    : `${patientWeight} kg = ${Math.round(parseFloat(patientWeight) * 2.20462)} lbs`
                  }
                </p>
              )}

              {totalDoseNeeded && (
                <Alert className="bg-orange-50 border-orange-200" data-testid="weight-based-result">
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="text-lg font-semibold text-orange-800">Total Dose Needed:</div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-3 bg-white rounded border border-orange-200">
                          <div className="text-2xl font-bold text-orange-700">{totalDoseNeeded.mcg} mcg</div>
                          <div className="text-sm text-orange-600">Micrograms per dose</div>
                        </div>
                        <div className="p-3 bg-white rounded border border-orange-200">
                          <div className="text-2xl font-bold text-orange-700">{totalDoseNeeded.mg} mg</div>
                          <div className="text-sm text-orange-600">Milligrams per dose</div>
                        </div>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Peptide-Specific Calculator */}
        <TabsContent value="peptide">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-teal-600" />
                Peptide-Specific Calculator
              </CardTitle>
              <CardDescription>
                Get dosing recommendations for specific peptides from our database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="selectPeptide">Select Peptide</Label>
                <Select value={selectedPeptide} onValueChange={setSelectedPeptide}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a peptide..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {peptidesDatabase && peptidesDatabase.map((peptide) => (
                      <SelectItem key={peptide.id || peptide.name} value={peptide.name}>
                        {peptide.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {peptideInfo && (
                <div className="space-y-4">
                  <Alert className="bg-teal-50 border-teal-200">
                    <AlertDescription>
                      <div className="space-y-3">
                        <div>
                          <div className="font-semibold text-teal-800 mb-1">{peptideInfo.name}</div>
                          {peptideInfo.category && <Badge variant="outline" className="text-xs mb-1">{peptideInfo.category}</Badge>}
                          {peptideInfo.mechanism_of_action && (
                            <div className="text-sm text-teal-700 mt-1">{peptideInfo.mechanism_of_action}</div>
                          )}
                          {peptideInfo.indications && peptideInfo.indications.length > 0 && (
                            <div className="text-sm text-teal-600 mt-1">
                              <span className="font-medium">Indications:</span> {Array.isArray(peptideInfo.indications) ? peptideInfo.indications.join(', ') : peptideInfo.indications}
                            </div>
                          )}
                        </div>

                        {peptideInfo.complete_dosing_schedule && (
                          <div className="border-t border-teal-200 pt-3">
                            <div className="font-medium text-teal-800 mb-1">Dosing Schedule:</div>
                            {typeof peptideInfo.complete_dosing_schedule === 'object' ? (
                              <div className="text-sm text-teal-700 space-y-1">
                                {Object.entries(peptideInfo.complete_dosing_schedule).map(([key, val]) => (
                                  <div key={key}><span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {typeof val === 'object' ? JSON.stringify(val) : String(val)}</div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-teal-700">{peptideInfo.complete_dosing_schedule}</div>
                            )}
                          </div>
                        )}

                        {peptideInfo.administration_techniques && (
                          <div className="border-t border-teal-200 pt-3">
                            <div className="font-medium text-teal-800 mb-1">Administration:</div>
                            {typeof peptideInfo.administration_techniques === 'object' ? (
                              <div className="text-sm text-teal-700 space-y-1">
                                {Object.entries(peptideInfo.administration_techniques).map(([key, val]) => (
                                  <div key={key}><span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {typeof val === 'object' ? JSON.stringify(val) : String(val)}</div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-teal-700">{peptideInfo.administration_techniques}</div>
                            )}
                          </div>
                        )}

                        {peptideInfo.safety_profile && (
                          <div className="border-t border-teal-200 pt-3">
                            <div className="font-medium text-teal-800 mb-1">Safety Profile:</div>
                            {typeof peptideInfo.safety_profile === 'object' ? (
                              <div className="text-sm text-teal-700 space-y-1">
                                {Object.entries(peptideInfo.safety_profile).map(([key, val]) => (
                                  <div key={key}><span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {Array.isArray(val) ? val.join(', ') : typeof val === 'object' ? JSON.stringify(val) : String(val)}</div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-teal-700">{peptideInfo.safety_profile}</div>
                            )}
                          </div>
                        )}

                        {peptideInfo.expected_timelines && (
                          <div className="border-t border-teal-200 pt-3">
                            <div className="font-medium text-teal-800 mb-1">Expected Timelines:</div>
                            {typeof peptideInfo.expected_timelines === 'object' ? (
                              <div className="text-sm text-teal-700 space-y-1">
                                {Object.entries(peptideInfo.expected_timelines).map(([key, val]) => (
                                  <div key={key}><span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {typeof val === 'object' ? JSON.stringify(val) : String(val)}</div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-teal-700">{peptideInfo.expected_timelines}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>

                  {relatedProtocols.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">Related Protocols</h4>
                      <div className="space-y-2">
                        {relatedProtocols.map((protocol, idx) => (
                          <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{protocol.name}</div>
                                <div className="text-sm text-muted-foreground">{protocol.indication}</div>
                              </div>
                              <ExternalLink className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Conversions */}
        <TabsContent value="convert">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-indigo-600" />
                Unit Conversion Tool
              </CardTitle>
              <CardDescription>
                Convert between different units commonly used in peptide therapy
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="convertValue">Value</Label>
                  <Input
                    id="convertValue"
                    type="number" min="0.01"
                    placeholder="1000"
                    value={convertValue}
                    onChange={positiveOnly(setConvertValue)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="convertFrom">From</Label>
                  <Select value={convertFromUnit} onValueChange={setConvertFromUnit}>
                    <SelectTrigger id="convertFrom">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mg">mg (milligrams)</SelectItem>
                      <SelectItem value="mcg">mcg (micrograms)</SelectItem>
                      <SelectItem value="g">g (grams)</SelectItem>
                      <SelectItem value="IU">IU (International Units)</SelectItem>
                      <SelectItem value="ml">ml (milliliters)</SelectItem>
                      <SelectItem value="units">units (syringe)</SelectItem>
                      <SelectItem value="lbs">lbs (pounds)</SelectItem>
                      <SelectItem value="kg">kg (kilograms)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="convertTo">To</Label>
                  <Select value={convertToUnit} onValueChange={setConvertToUnit}>
                    <SelectTrigger id="convertTo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mg">mg (milligrams)</SelectItem>
                      <SelectItem value="mcg">mcg (micrograms)</SelectItem>
                      <SelectItem value="g">g (grams)</SelectItem>
                      <SelectItem value="IU">IU (International Units)</SelectItem>
                      <SelectItem value="ml">ml (milliliters)</SelectItem>
                      <SelectItem value="units">units (syringe)</SelectItem>
                      <SelectItem value="lbs">lbs (pounds)</SelectItem>
                      <SelectItem value="kg">kg (kilograms)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={performUnitConversion} className="w-full">
                Convert
              </Button>

              {convertedValue && (
                <Alert className="bg-indigo-50 border-indigo-200">
                  <AlertDescription>
                    <div className="text-center">
                      <div className="text-sm text-indigo-600 mb-2">Result:</div>
                      <div className="text-3xl font-bold text-indigo-800">
                        {convertValue} {convertFromUnit} = {convertedValue} {convertToUnit}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Common Conversions Reference */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Quick Reference:</h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div>• 1 mg = 1,000 mcg</div>
                  <div>• 1 ml = 100 units</div>
                  <div>• 1 kg = 2.20462 lbs</div>
                  <div>• 1 IU ≈ 0.0347 mcg (peptide-specific)</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Disclaimer */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertDescription className="text-sm text-yellow-800">
          <div className="font-semibold mb-1">⚠️ Medical Disclaimer</div>
          This calculator is for educational and informational purposes only. Always consult with a licensed healthcare provider 
          before starting any peptide therapy. Dosages should be verified by a qualified medical professional based on individual 
          patient needs, lab work, and medical history.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default DosageCalculator;
